import { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { useBottomTabOverflow } from "@/components/ui/TabBarBackground";
import { useTheme } from "@/hooks/useTheme";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { profileService } from "@/services/profileService";
import useDateTime from "@/hooks/useDateTime";
import Head from "expo-router/head";

export default function UserSettingsPage() {
  const { session } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const bottom = useBottomTabOverflow();
  const { updateSettings } = useDateTime();

  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    preferred_name: "",
    email: "",
    state: "",
    city: "",
    timezone: "",
    time: "",
    format: "",
    email_request: "",
  });
  const [originalData, setOriginalData] = useState({
    first_name: "",
    last_name: "",
    preferred_name: "",
    email: "",
    state: "",
    city: "",
    timezone: "",
    time: "",
    format: "",
    email_request: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initial load of user data
  useEffect(() => {
    let mounted = true;
    if (!session?.user.id) return;
    (async () => {
      try {
        const profile = await profileService.getProfile(session.user.id);
        if (!mounted) return;

        const initialData = {
          first_name: profile.first_name || "",
          last_name: profile.last_name || "",
          preferred_name: profile.preferred_name || "",
          email: session?.user?.email || "",
          state: profile.meta_data?.state || "",
          city: profile.meta_data?.city || "",
          timezone: profile.meta_data?.timezone || "",
          time: profile.meta_data?.time || "",
          format: profile.meta_data?.format || "",
          email_request: profile.meta_data?.email_request || "",
        };
        setUserData(initialData);
        setOriginalData(initialData);
        setLoading(false);
      } catch (error) {
        toast.showToast({
          title: "Couldn't fetch",
          message: error.message,
          toastType: "error",
        });
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, session?.user.email]);

  const handleSave = async () => {
    if (!userData.first_name || !userData.email) {
      toast.showToast({
        title: "Missing fields",
        message: "First name and email are required.",
        toastType: "error",
      });
      return;
    }
    setSaving(true);
    try {
      await profileService.updateFullProfile(session?.user?.id!, {
        profile: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          preferred_name: userData.preferred_name,
        },
        metaData: {
          state: userData.state,
          city: userData.city,
          timezone: userData.timezone,
          time: userData.time,
          format: userData.format,
          email_request: userData.email_request,
        },
      });

      // Update the baseline after successful save
      setOriginalData({ ...userData });

      // Update DateTime context with new settings
      updateSettings({
        timezone: userData.timezone,
        dateFormat: userData.format,
        timeFormat: userData.time as "12" | "24",
      });

      toast.showToast({
        title: "Profile saved",
        message: "Your changes have been saved.",
        toastType: "success",
      });
    } catch (e: any) {
      toast.showToast({
        title: "Save failed",
        message: e?.message || "Unable to save profile.",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const isDirty = useMemo(() => {
    return JSON.stringify(userData) !== JSON.stringify(originalData);
  }, [userData, originalData]);

  const handleReset = () => {
    setUserData({ ...originalData });
  };

  return (
    <>
      <Head>
        <title>RockForce Settings User</title>
        <meta name="description" content="" />
      </Head>
      <ScrollView
        scrollEventThrottle={16}
        scrollIndicatorInsets={{ bottom }}
        contentContainerStyle={{ gap: 16, paddingBottom: bottom }}
      >
        {/* Header with Save/Reset buttons */}
        <View style={styles.header}>
          <ThemedText type="title">User Settings</ThemedText>
          <View style={styles.headerActions}>
            <ThemedText style={{ opacity: isDirty ? 1 : 0.5 }}>
              {isDirty ? "• Unsaved changes" : "All changes saved"}
            </ThemedText>
            <Button
              text="Reset"
              icon="backup-restore"
              style={[styles.actionBtn, styles.ghostBtn, { borderColor: colors.border }]}
              textStyle={{ fontSize: 16 }}
              disabled={!isDirty}
              onPress={handleReset}
            />
            <Button
              text={saving ? "Saving..." : "Save Changes"}
              iconColor="white"
              icon="content-save"
              style={[styles.actionBtn, { backgroundColor: colors.primary }]}
              textStyle={{ fontSize: 16, color: "white" }}
              disabled={saving || !isDirty}
              onPress={handleSave}
            />
          </View>
        </View>

        {/* Profile Information Section */}
        <View style={[{ borderColor: colors.border }, styles.stepContainer]}>
          {/* Left block: title */}
          <View style={styles.containerLeft}>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <ThemedText type="subtitle">Profile Information</ThemedText>
            </View>
            <ThemedText type="thin">Personal details and account info</ThemedText>
          </View>

          {/* Right block: Inputs */}
          <View style={styles.containerRight}>
            <ThemedInput
              style={styles.input}
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              label={"First Name"}
              value={userData.first_name}
              onChangeText={(text) => setUserData((prev) => ({ ...prev, first_name: text }))}
            />
            <ThemedInput
              style={styles.input}
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              label={"Last Name"}
              value={userData.last_name}
              onChangeText={(text) => setUserData((prev) => ({ ...prev, last_name: text }))}
            />
            <ThemedInput
              style={styles.input}
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              label={"Preferred Name"}
              value={userData.preferred_name}
              onChangeText={(text) => setUserData((prev) => ({ ...prev, preferred_name: text }))}
            />
            <ThemedInput
              style={styles.input}
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              label={"Current Email"}
              value={session?.user?.email || ""}
              editable={false}
              placeholder="Current email from session"
            />
            <ThemedInput
              style={styles.input}
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              label={"Email Change Request"}
              value={userData.email_request}
              onChangeText={(text) => setUserData((prev) => ({ ...prev, email_request: text }))}
              placeholder="Enter new email to request change"
            />
          </View>
        </View>

        {/* Location & Timezone Section */}
        <View style={[{ borderColor: colors.border }, styles.stepContainer]}>
          {/* Left block: title */}
          <View style={styles.containerLeft}>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <ThemedText type="subtitle">Location & Timezone</ThemedText>
            </View>
            <ThemedText type="thin">Regional settings and preferences</ThemedText>
          </View>

          {/* Right block: Inputs */}
          <View style={styles.containerRight}>
            {loading ? (
              <ThemedText type="thin" style={{ opacity: 0.8 }}>
                Loading...
              </ThemedText>
            ) : (
              <>
                <ThemedInput
                  style={styles.input}
                  inputStyle={styles.inputStyle}
                  labelStyle={styles.labelStyle}
                  label={"State"}
                  value={userData.state}
                  onChangeText={(text) => setUserData((prev) => ({ ...prev, state: text }))}
                />
                <ThemedInput
                  style={styles.input}
                  inputStyle={styles.inputStyle}
                  labelStyle={styles.labelStyle}
                  label={"City"}
                  value={userData.city}
                  onChangeText={(text) => setUserData((prev) => ({ ...prev, city: text }))}
                />
                <ThemedInput
                  style={styles.input}
                  inputStyle={styles.inputStyle}
                  labelStyle={styles.labelStyle}
                  label={"Timezone"}
                  value={userData.timezone}
                  onChangeText={(text) => setUserData((prev) => ({ ...prev, timezone: text }))}
                  items={[
                    { text: "America/New_York", bgColor: "#3b82f6" },
                    { text: "America/Chicago", bgColor: "#3b82f6" },
                    { text: "America/Denver", bgColor: "#3b82f6" },
                    { text: "America/Los_Angeles", bgColor: "#3b82f6" },
                    { text: "Europe/London", bgColor: "#3b82f6" },
                    { text: "Europe/Paris", bgColor: "#3b82f6" },
                    { text: "Europe/Berlin", bgColor: "#3b82f6" },
                    { text: "Asia/Tokyo", bgColor: "#3b82f6" },
                    { text: "Asia/Shanghai", bgColor: "#3b82f6" },
                    { text: "Australia/Sydney", bgColor: "#3b82f6" },
                  ]}
                />
                <ThemedInput
                  style={styles.input}
                  inputStyle={styles.inputStyle}
                  labelStyle={styles.labelStyle}
                  label={"Date Format"}
                  value={userData.format}
                  onChangeText={(text) => setUserData((prev) => ({ ...prev, format: text }))}
                  items={[
                    { text: "MM/dd/yyyy", bgColor: "#10b981" },
                    { text: "dd/MM/yyyy", bgColor: "#10b981" },
                    { text: "yyyy-MM-dd", bgColor: "#10b981" },
                    { text: "MMM d, yyyy", bgColor: "#10b981" },
                    { text: "d MMM yyyy", bgColor: "#10b981" },
                  ]}
                />
                <ThemedInput
                  style={styles.input}
                  inputStyle={styles.inputStyle}
                  labelStyle={styles.labelStyle}
                  label={"Time Format"}
                  value={userData.time}
                  onChangeText={(text) => setUserData((prev) => ({ ...prev, time: text }))}
                  items={[
                    { text: "12", bgColor: "#8b5cf6" },
                    { text: "24", bgColor: "#8b5cf6" },
                  ]}
                />
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 8,
    paddingBottom: 16,
    gap: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(0,0,0,0.1)",
  },
  headerActions: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  stepContainer: {
    gap: 16,
    flexDirection: "row",
    flexWrap: "wrap",
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  containerLeft: {
    flex: 1,
    gap: 8,
    flexBasis: "10%",
    minWidth: 200,
    flexGrow: 1,
  },
  containerRight: {
    flex: 1,
    gap: 8,
    flexGrow: 1,
    alignItems: "flex-end",
    flexBasis: "50%",
  },
  input: {
    flex: 1,
    flexGrow: 1,
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    gap: 8,
  },
  labelStyle: { flexBasis: "20%", minWidth: 100 },
  inputStyle: {},
  actionBtn: {
    minHeight: 40,
    gap: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  ghostBtn: {
    backgroundColor: "transparent",
    borderWidth: 1,
  },
});

// The code has been restructured to match the pattern in integrations.tsx. Next steps include setting up the supabase data fetch and handling timezone/location.
